import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Ban, CheckCircle, AlertTriangle, MoreVertical } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchConsumers, fetchAdmins, deleteUser, suspendUser } from '../../store/users/usersSlice';
import { Button, TableRowSkeleton, EmptyState, useToast } from '../../components/common';
import { ZoneLabels, Zone, User } from '../../types';

export const ManageUsersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { consumers, admins, isLoading } = useAppSelector((state) => state.users);
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState<'consumers' | 'admins'>('consumers');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Action states
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchConsumers());
    dispatch(fetchAdmins());
  }, [dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const users = activeTab === 'consumers' ? consumers : admins;

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
    setOpenDropdown(null);
  };

  const handleSuspendClick = (user: User) => {
    setSelectedUser(user);
    setShowSuspendModal(true);
    setOpenDropdown(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      await dispatch(deleteUser(selectedUser._id)).unwrap();
      showToast(`${selectedUser.firstName} ${selectedUser.lastName} has been deleted`, 'success');
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (error: any) {
      showToast(error || 'Failed to delete user', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmSuspend = async () => {
    if (!selectedUser) return;

    const shouldSuspend = selectedUser.isActive;
    setActionLoading(true);
    try {
      await dispatch(suspendUser({ userId: selectedUser._id, suspend: shouldSuspend })).unwrap();
      showToast(
        `${selectedUser.firstName} ${selectedUser.lastName} has been ${shouldSuspend ? 'suspended' : 'activated'}`,
        'success'
      );
      setShowSuspendModal(false);
      setSelectedUser(null);
    } catch (error: any) {
      showToast(error || 'Failed to update user status', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleDropdown = (userId: string) => {
    setOpenDropdown(openDropdown === userId ? null : userId);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-1 sm:mb-2">Manage Users</h1>
          <p className="text-sm sm:text-base text-slate-500">View and manage users</p>
        </div>
        <Link to="/admin/users/add-admin" className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto justify-center">
            <Plus size={18} className="mr-1 sm:mr-2 sm:w-5 sm:h-5" />
            Add Admin
          </Button>
        </Link>
      </div>

      <div className="flex border-b border-slate-200 mb-4 sm:mb-6">
        <button
          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'consumers'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => setActiveTab('consumers')}
        >
          Members ({consumers.length})
        </button>
        <button
          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'admins'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => setActiveTab('admins')}
        >
          Admins ({admins.length})
        </button>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">
                    {activeTab === 'consumers' ? 'Campus / Zone' : 'Title'}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">
                    {activeTab === 'consumers' ? 'Status' : 'Added By'}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Joined</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRowSkeleton key={i} columns={6} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : users.length === 0 ? (
        <EmptyState
          variant="users"
          title={`No ${activeTab} found`}
          description={activeTab === 'consumers' ? "Users who sign up will appear here." : "Add administrators to help manage the library."}
          action={activeTab === 'admins' ? { label: 'Add Admin', onClick: () => window.location.href = '/admin/users/add-admin' } : undefined}
        />
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3">
            {users.map((user) => (
              <div key={user._id} className="bg-white rounded-xl shadow-sm p-4 relative">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium overflow-hidden flex-shrink-0">
                    {user.profileImage ? (
                      <img src={user.profileImage} alt={user.firstName} className="w-full h-full object-cover" />
                    ) : (
                      <>{user.firstName.charAt(0)}{user.lastName.charAt(0)}</>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{user.firstName} {user.lastName}</h3>
                        <p className="text-sm text-slate-600 truncate">{user.email}</p>
                      </div>
                      {user._id !== currentUser?._id && (
                        <div className="relative flex-shrink-0" ref={openDropdown === user._id ? dropdownRef : null}>
                          <button
                            onClick={() => toggleDropdown(user._id)}
                            className="p-1.5 rounded hover:bg-slate-100 transition-colors"
                          >
                            <MoreVertical size={18} className="text-slate-500" />
                          </button>
                          {openDropdown === user._id && (
                            <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                              <button
                                onClick={() => handleSuspendClick(user)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                              >
                                {user.isActive ? (
                                  <>
                                    <Ban size={16} className="text-amber-500" />
                                    <span>Suspend</span>
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle size={16} className="text-green-500" />
                                    <span>Activate</span>
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteClick(user)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-red-600"
                              >
                                <Trash2 size={16} />
                                <span>Delete</span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 space-y-2 text-sm">
                      {activeTab === 'consumers' ? (
                        <>
                          {(user.campus || user.zone) && (
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">Location:</span>
                              <span className="text-slate-900">
                                {user.campus && <span>{user.campus}</span>}
                                {user.campus && user.zone && <span> / </span>}
                                {user.zone && <span className="text-primary-600 font-medium">{ZoneLabels[user.zone as Zone]}</span>}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="text-slate-500">Status:</span>
                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              user.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          {user.title && (
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">Title:</span>
                              <span className="text-slate-900">{user.title}</span>
                            </div>
                          )}
                          {user.addedBy && (
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">Added by:</span>
                              <span className="text-slate-900">{user.addedBy.firstName} {user.addedBy.lastName}</span>
                            </div>
                          )}
                        </>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">Joined:</span>
                        <span className="text-slate-900">{new Date(user.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="mt-4">
                        <Link
                          to={`/admin/users/${user._id}`}
                          className="inline-flex items-center gap-2 text-primary-600 text-sm font-medium hover:text-primary-700"
                        >
                          View student details
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">
                    {activeTab === 'consumers' ? 'Campus / Zone' : 'Title'}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">
                    {activeTab === 'consumers' ? 'Status' : 'Added By'}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Joined</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {users.map((user) => (
                  <tr key={user._id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-medium overflow-hidden">
                          {user.profileImage ? (
                            <img src={user.profileImage} alt={user.firstName} className="w-full h-full object-cover" />
                          ) : (
                            <>{user.firstName.charAt(0)}{user.lastName.charAt(0)}</>
                          )}
                        </div>
                        <span className="text-sm text-slate-900">{user.firstName} {user.lastName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-[200px] truncate">{user.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-[180px]">
                      {activeTab === 'consumers' ? (
                        <div>
                          {user.campus && <span>{user.campus}</span>}
                          {user.campus && user.zone && <span> / </span>}
                          {user.zone && <span className="text-primary-600 font-medium">{ZoneLabels[user.zone as Zone]}</span>}
                          {!user.campus && !user.zone && <span className="text-slate-400">-</span>}
                        </div>
                      ) : (
                        <span>{user.title || <span className="text-slate-400">-</span>}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {activeTab === 'consumers' ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          user.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-600">
                          {user.addedBy ? `${user.addedBy.firstName} ${user.addedBy.lastName}` : <span className="text-slate-400">-</span>}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 space-y-2">
                      <Link
                        to={`/admin/users/${user._id}`}
                        className="inline-flex text-primary-600 text-sm font-medium hover:text-primary-700"
                      >
                        View details
                      </Link>
                      {user._id !== currentUser?._id && (
                        <div className="relative" ref={openDropdown === user._id ? dropdownRef : null}>
                          <button
                            onClick={() => toggleDropdown(user._id)}
                            className="p-1 rounded hover:bg-slate-100 transition-colors"
                          >
                            <MoreVertical size={18} className="text-slate-500" />
                          </button>
                          {openDropdown === user._id && (
                            <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                              <button
                                onClick={() => handleSuspendClick(user)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                              >
                                {user.isActive ? (
                                  <>
                                    <Ban size={16} className="text-amber-500" />
                                    <span>Suspend</span>
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle size={16} className="text-green-500" />
                                    <span>Activate</span>
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteClick(user)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-red-600"
                              >
                                <Trash2 size={16} />
                                <span>Delete</span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Delete User</h3>
            </div>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>?
              This action cannot be undone and all their data will be permanently removed.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmDelete}
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete User'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Confirmation Modal */}
      {showSuspendModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                selectedUser.isActive ? 'bg-amber-100' : 'bg-green-100'
              }`}>
                {selectedUser.isActive ? (
                  <Ban size={20} className="text-amber-600" />
                ) : (
                  <CheckCircle size={20} className="text-green-600" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedUser.isActive ? 'Suspend User' : 'Activate User'}
              </h3>
            </div>
            <p className="text-slate-600 mb-6">
              {selectedUser.isActive ? (
                <>
                  Are you sure you want to suspend <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>?
                  They will not be able to access the library until reactivated.
                </>
              ) : (
                <>
                  Are you sure you want to activate <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>?
                  They will regain access to the library.
                </>
              )}
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowSuspendModal(false);
                  setSelectedUser(null);
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                variant={selectedUser.isActive ? 'danger' : 'primary'}
                onClick={handleConfirmSuspend}
                disabled={actionLoading}
              >
                {actionLoading
                  ? (selectedUser.isActive ? 'Suspending...' : 'Activating...')
                  : (selectedUser.isActive ? 'Suspend User' : 'Activate User')
                }
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
